import React, { useEffect, useMemo, useState } from "react";
import {
  alpha,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PaidOutlinedIcon from "@mui/icons-material/PaidOutlined";
import dayjs from "dayjs";
import AppDatePicker from "../../../Components/DatePicker/AppDatePicker";
import {
  useCreatePaymentMutation,
  useGetPaymentMethodQuery,
} from "../../../Api/attoDeskApi";
import { useNotifier } from "../../../Core/Notifier";

interface Props {
  invoice: any | null;
  open: boolean;
  onClose: () => void;
}

const money = (v: number | string | null | undefined) =>
  `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const RecordPaymentDialog: React.FC<Props> = ({ invoice, open, onClose }) => {
  const { showMessage, showErrorMessage } = useNotifier();
  const [createPayment, { isLoading: saving }] = useCreatePaymentMutation();
  const {
    data: methodsData,
    isLoading: methodsLoading,
    isError: methodsError,
  } = useGetPaymentMethodQuery({ searchText: "" }, { skip: !open });

  const methods: any[] = useMemo(() => {
    const arr = (methodsData as any)?.data;
    return Array.isArray(arr) ? arr : [];
  }, [methodsData]);

  const balance = Number(invoice?.balance_due ?? invoice?.totalAmount ?? 0);
  const [amount, setAmount] = useState<string>("");
  const [date, setDate] = useState<string>(dayjs().format("YYYY-MM-DD"));
  const [methodId, setMethodId] = useState<string>("");
  const [reference, setReference] = useState<string>("");
  const [notes, setNotes] = useState<string>("");
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (open) {
      setAmount(balance.toFixed(2));
      setDate(dayjs().format("YYYY-MM-DD"));
      setMethodId(methods.find((m) => m.isDefault)?.id?.toString() ?? "");
      setReference("");
      setNotes("");
      setErrors({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, invoice?.id, methods.length]);

  if (!invoice) return null;

  const validate = () => {
    const e: Record<string, string> = {};
    const n = Number(amount);
    if (!Number.isFinite(n) || n <= 0) e.amount = "Enter a payment amount greater than zero.";
    if (n > balance + 0.005) e.amount = `Payment cannot exceed the remaining balance of ${money(balance)}.`;
    if (!date) e.date = "Payment date is required.";
    if (!methodId) e.method = "Select a payment method.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async () => {
    if (!validate() || saving) return;
    try {
      const r: any = await createPayment({
        invoiceId: invoice.id,
        studentId: invoice.studentId,
        parentId: invoice.parent_info?.id ?? invoice.parentId ?? null,
        amount: Number(amount),
        paymentDate: date,
        paymentMethodId: methodId || null,
        paymentType: Number(amount) >= balance ? "full" : "partial",
        transactionId: reference || undefined,
        notes: notes || undefined,
      }).unwrap();
      if (r?.status || r?.data?.payment) {
        showMessage(`Payment of ${money(amount)} recorded.`);
        onClose();
      } else {
        showErrorMessage(r?.message || "Failed to record payment.");
      }
    } catch (e: any) {
      showErrorMessage(e?.data?.message || "Failed to record payment.");
    }
  };

  const handleClose = (_: unknown, reason?: string) => {
    if (saving && (reason === "backdropClick" || reason === "escapeKeyDown")) return;
    onClose();
  };

  const studentName = invoice.studentName || (invoice.student ? `${invoice.student.first_name || ""} ${invoice.student.last_name || ""}`.trim() : "—");

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", pr: 1 }}>
        <Box>
          <Typography sx={{ fontSize: 18, fontWeight: 800, letterSpacing: "-0.01em" }}>Record Payment</Typography>
          <Typography sx={{ fontSize: 12.5, color: "#64748b", mt: 0.25 }}>
            Invoice {invoice.invoiceNumber} · {studentName}
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose} disabled={saving}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {/* Summary strip */}
        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 1, mb: 2 }}>
          <Box sx={{ p: 1.25, borderRadius: "10px", bgcolor: alpha("#2563eb", 0.08), border: `1px solid ${alpha("#2563eb", 0.18)}` }}>
            <Typography sx={{ fontSize: 10, fontWeight: 800, textTransform: "uppercase", color: "#1d4ed8" }}>Total</Typography>
            <Typography sx={{ fontSize: 15, fontWeight: 800, color: "#1e3a8a", mt: 0.25 }}>{money(invoice.totalAmount)}</Typography>
          </Box>
          <Box sx={{ p: 1.25, borderRadius: "10px", bgcolor: alpha("#16a34a", 0.08), border: `1px solid ${alpha("#16a34a", 0.18)}` }}>
            <Typography sx={{ fontSize: 10, fontWeight: 800, textTransform: "uppercase", color: "#15803d" }}>Already Paid</Typography>
            <Typography sx={{ fontSize: 15, fontWeight: 800, color: "#166534", mt: 0.25 }}>{money(invoice.paid_amount)}</Typography>
          </Box>
          <Box sx={{ p: 1.25, borderRadius: "10px", bgcolor: alpha("#f59e0b", 0.08), border: `1px solid ${alpha("#f59e0b", 0.2)}` }}>
            <Typography sx={{ fontSize: 10, fontWeight: 800, textTransform: "uppercase", color: "#b45309" }}>Balance</Typography>
            <Typography sx={{ fontSize: 15, fontWeight: 800, color: "#92400e", mt: 0.25 }}>{money(balance)}</Typography>
          </Box>
        </Box>

        {/* Fields */}
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1.5 }}>
          <TextField
            label="Payment Amount"
            required
            size="small"
            value={amount}
            onChange={(e) => setAmount(e.target.value.replace(/[^0-9.]/g, ""))}
            error={!!errors.amount}
            helperText={errors.amount || `Max ${money(balance)}`}
            InputProps={{
              startAdornment: <InputAdornment position="start">$</InputAdornment>,
            }}
          />
          <AppDatePicker
            label="Payment Date"
            required
            value={date}
            onChange={(v) => setDate(v)}
            helperText={errors.date}
            error={!!errors.date}
            fullWidth
          />
          <FormControl size="small" fullWidth required error={!!errors.method}>
            <InputLabel>Payment Method</InputLabel>
            <Select
              label="Payment Method"
              value={methodId}
              onChange={(e) => setMethodId(String(e.target.value))}
              disabled={methodsLoading}
            >
              {methodsLoading && <MenuItem value="">Loading payment methods…</MenuItem>}
              {!methodsLoading && methodsError && (
                <MenuItem value="">Unable to load payment methods</MenuItem>
              )}
              {!methodsLoading && !methodsError && methods.length === 0 && (
                <MenuItem value="">No payment methods configured</MenuItem>
              )}
              {!methodsLoading && !methodsError && methods.length > 0 && (
                <MenuItem value="">— Select —</MenuItem>
              )}
              {methods.map((m: any) => (
                <MenuItem key={m.id} value={String(m.id)}>
                  {m.name || m.methodType}
                </MenuItem>
              ))}
            </Select>
            {errors.method && (
              <Typography sx={{ fontSize: 11, color: "#d32f2f", mt: 0.5, ml: 1.75 }}>
                {errors.method}
              </Typography>
            )}
          </FormControl>
          <TextField
            label="Reference #"
            size="small"
            value={reference}
            onChange={(e) => setReference(e.target.value)}
            placeholder="Cheque / transaction id"
          />
          <TextField
            label="Notes"
            size="small"
            fullWidth
            multiline
            rows={2}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            sx={{ gridColumn: { sm: "1 / span 2" } }}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Button onClick={onClose} disabled={saving} color="inherit">Cancel</Button>
        <Button
          onClick={submit}
          variant="contained"
          disabled={saving}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <PaidOutlinedIcon />}
          sx={{
            textTransform: "none",
            fontWeight: 700,
            borderRadius: "10px",
            background: "linear-gradient(135deg, #16a34a 0%, #15803d 100%)",
            "&:hover": { boxShadow: "0 6px 18px rgba(22,163,74,0.35)" },
          }}
        >
          {saving ? "Recording…" : "Record Payment"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RecordPaymentDialog;
